import { useState } from "react";
import { format } from "date-fns";
import {
  CreditCard,
  Ticket,
  ChevronDown,
  ChevronRight,
  Loader2,
  RotateCcw,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  EnrollmentBillingItem,
  EnrollmentRefundDetail,
  EnrollmentSageTransaction,
} from "../../api/getEnrollmentBilling/types";
import axiosInstance from "@/api/axiosInstance";
import { useToast } from "@/hooks/useToast";
import { retrySageTransactionSync } from "../../api/retrySageTransactionSync/retrySageTransactionSyncApi";

interface EnrollmentBillingDrawerProps {
  enrollment: EnrollmentBillingItem | null;
  open: boolean;
  onClose: () => void;
  onRefundSuccess: () => void;
}

function fmt(amount: string | number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(typeof amount === "string" ? parseFloat(amount) : amount);
}

function fmtDate(dateString: string | null) {
  if (!dateString) return "—";
  try {
    return format(new Date(dateString), "MMM d, yyyy");
  } catch {
    return dateString;
  }
}

const paymentStatusLabels: Record<string, string> = {
  paid: "Paid",
  unpaid: "Unpaid",
};

const paymentStatusStyles: Record<string, string> = {
  paid: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300",
  unpaid: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

function CollapsibleSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div>
      <button
        className="flex items-center gap-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors w-full text-left py-1"
        onClick={() => setOpen(!open)}
        type="button"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        {title}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

export function EnrollmentBillingDrawer({
  enrollment,
  open,
  onClose,
  onRefundSuccess,
}: EnrollmentBillingDrawerProps) {
  const { toast } = useToast();
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [showVoucherReturnForm, setShowVoucherReturnForm] = useState(false);
  const [refundReason, setRefundReason] = useState("");
  const [voucherReturnReason, setVoucherReturnReason] = useState("");
  const [isRefunding, setIsRefunding] = useState(false);
  const [isReturningVoucher, setIsReturningVoucher] = useState(false);

  function handleOpenChange(isOpen: boolean) {
    if (!isOpen) {
      setShowRefundForm(false);
      setShowVoucherReturnForm(false);
      setRefundReason("");
      setVoucherReturnReason("");
      onClose();
    }
  }

  if (!enrollment) return null;

  const paymentStatusLabel =
    paymentStatusLabels[enrollment.paymentStatus] || enrollment.paymentStatus;
  const paymentStatusStyle =
    paymentStatusStyles[enrollment.paymentStatus] || paymentStatusStyles.unpaid;
  const seatPrice = parseFloat(enrollment.seatPrice);
  const totalDiscount = parseFloat(enrollment.totalDiscountAmount);
  const netAmount = seatPrice - totalDiscount;
  const refundedAmount = parseFloat(enrollment.refundedAmount);
  const pendingRefundAmount = parseFloat(enrollment.pendingRefundAmount);
  const remainingRefundable = parseFloat(enrollment.remainingRefundableAmount);
  const isCardPayment = enrollment.paymentMethod === "card";
  const canRefund = isCardPayment && remainingRefundable > 0;
  const canReturnVoucher =
    enrollment.paymentMethod === "voucher" && enrollment.voucherIsActive;
  const failedSyncCount = enrollment.sageTransactions.filter(
    (txn) => txn.syncStatus === "failed",
  ).length;

  return (
    <Sheet onOpenChange={handleOpenChange} open={open}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" side="right">
        <SheetHeader className="pb-4">
          <div className="flex items-start justify-between gap-2 pr-6">
            <div>
              <SheetTitle className="text-base font-normal text-gray-900 dark:text-white">
                {enrollment.firstName} {enrollment.lastName}
              </SheetTitle>
              <SheetDescription className="text-xs mt-1">
                {enrollment.email}
                {enrollment.companyName && ` · ${enrollment.companyName}`}
                {enrollment.confirmationNumber &&
                  ` · #${enrollment.confirmationNumber}`}
              </SheetDescription>
            </div>
            <span
              className={`text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0 mt-1 ${paymentStatusStyle}`}
            >
              {paymentStatusLabel}
            </span>
          </div>
        </SheetHeader>

        <div className="space-y-5">
          {/* Cancellation Notice */}
          {enrollment.isCancelled && !enrollment.refunded && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/20 p-3">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                This enrollment has been cancelled
              </p>
              {enrollment.cancellationReason && (
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                  Reason: {enrollment.cancellationReason}
                </p>
              )}
              <p className="text-xs text-amber-600 dark:text-amber-400/80 mt-2">
                {isCardPayment
                  ? "Consider issuing a refund below."
                  : "Consider returning the voucher below."}
              </p>
            </div>
          )}

          {/* Sync Failure Notice */}
          {failedSyncCount > 0 && (
            <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/20 p-3 flex items-start gap-2.5">
              <AlertTriangle className="h-4 w-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800 dark:text-red-300">
                  Intacct sync failed
                </p>
                <p className="text-xs text-red-600 dark:text-red-400/80 mt-0.5">
                  {failedSyncCount === 1
                    ? "A transaction failed to sync. See Sage Transactions below for details."
                    : `${failedSyncCount} transactions failed to sync. See Sage Transactions below for details.`}
                </p>
              </div>
            </div>
          )}

          {/* Financial Summary */}
          <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/50 p-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">
                Seat Price
              </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {fmt(seatPrice)}
              </span>
            </div>
            {totalDiscount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  Discount
                  {enrollment.discounts[0]?.code && (
                    <span className="font-mono text-xs ml-1">
                      ({enrollment.discounts[0].code})
                    </span>
                  )}
                </span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  -{fmt(totalDiscount)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-500 dark:text-gray-400">
                Net Amount
              </span>
              <span className="font-medium text-gray-900 dark:text-white">
                {fmt(netAmount)}
              </span>
            </div>
            {refundedAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  Refunded
                </span>
                <span className="font-medium text-red-600 dark:text-red-400">
                  -{fmt(refundedAmount)}
                </span>
              </div>
            )}
            {pendingRefundAmount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">
                  Pending Refund
                </span>
                <span className="font-medium text-amber-600 dark:text-amber-400">
                  -{fmt(pendingRefundAmount)}
                </span>
              </div>
            )}
            {isCardPayment && (
              <>
                <Separator className="!my-2" />
                <div className="flex justify-between text-sm">
                  <span className="font-medium text-gray-900 dark:text-white">
                    Remaining Refundable
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {fmt(remainingRefundable)}
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Refund Details */}
          {enrollment.refundDetails.length > 0 && (
            <>
              <Separator />
              <CollapsibleSection
                defaultOpen
                title={`Refund Details (${enrollment.refundDetails.length})`}
              >
                <div className="space-y-2">
                  {enrollment.refundDetails.map(
                    (refund: EnrollmentRefundDetail, i: number) => (
                      <div
                        className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 text-sm"
                        key={`refund-${i}`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <span className="font-medium text-gray-900 dark:text-white">
                            {fmt(refund.amount)}
                          </span>
                          {refund.attendeeNotified !== null && (
                            <span
                              className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                refund.attendeeNotified
                                  ? "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300"
                                  : "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400"
                              }`}
                            >
                              {refund.attendeeNotified
                                ? "Notified"
                                : "Not Notified"}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                          {refund.transactionId && (
                            <p>
                              Transaction:{" "}
                              <span className="font-mono text-gray-900 dark:text-white">
                                {refund.transactionId}
                              </span>
                            </p>
                          )}
                          {refund.reason && <p>Reason: {refund.reason}</p>}
                          {refund.refundedBy && (
                            <p>Refunded by: {refund.refundedBy}</p>
                          )}
                          {refund.refundedAt && (
                            <p>Date: {fmtDate(refund.refundedAt)}</p>
                          )}
                        </div>
                      </div>
                    ),
                  )}
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* Payment Method */}
          {isCardPayment && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Payment Method
              </h4>
              <div className="flex items-center gap-2 text-sm">
                <CreditCard className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-gray-900 dark:text-white">
                  {enrollment.cardType || "Card"} ····{" "}
                  {enrollment.cardLast4 || "****"}
                </span>
              </div>
              {(enrollment.transactionId || enrollment.customerProfileId) && (
                <CollapsibleSection title="Transaction Details">
                  <div className="space-y-1 text-xs">
                    {enrollment.transactionId && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Transaction ID
                        </span>
                        <span className="font-mono text-gray-900 dark:text-white">
                          {enrollment.transactionId}
                        </span>
                      </div>
                    )}
                    {enrollment.customerProfileId && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Customer Profile
                        </span>
                        <span className="font-mono text-gray-900 dark:text-white">
                          {enrollment.customerProfileId}
                        </span>
                      </div>
                    )}
                    {enrollment.paymentProfileId && (
                      <div className="flex justify-between">
                        <span className="text-gray-500 dark:text-gray-400">
                          Payment Profile
                        </span>
                        <span className="font-mono text-gray-900 dark:text-white">
                          {enrollment.paymentProfileId}
                        </span>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              )}
            </div>
          )}

          {/* Voucher Details */}
          {enrollment.usedVoucher && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                Voucher
              </h4>
              <div className="flex items-center gap-2 text-sm">
                <Ticket className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <span className="text-gray-900 dark:text-white">
                  {enrollment.voucherName || "Voucher"}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                {enrollment.voucherRedeemedAt && (
                  <p>Redeemed: {fmtDate(enrollment.voucherRedeemedAt)}</p>
                )}
                {enrollment.voucherReturnedAt ? (
                  <>
                    <p>Returned: {fmtDate(enrollment.voucherReturnedAt)}</p>
                    {enrollment.voucherReturnReason && (
                      <p className="italic">
                        Reason: {enrollment.voucherReturnReason}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-gray-400 dark:text-gray-500">Active</p>
                )}
              </div>
            </div>
          )}

          {/* Discounts (if multiple) */}
          {enrollment.discounts.length > 1 && (
            <>
              <Separator />
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Discounts
                </h4>
                {enrollment.discounts.map((d, i) => (
                  <div
                    className="flex justify-between text-sm"
                    key={`${d.source}-${i}`}
                  >
                    <span className="text-gray-500 dark:text-gray-400">
                      {d.code ? (
                        <span className="font-mono text-xs">{d.code}</span>
                      ) : (
                        d.source
                      )}
                      {d.reason && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                          — {d.reason}
                        </span>
                      )}
                    </span>
                    <span className="text-green-600 dark:text-green-400">
                      -{fmt(d.appliedAmount)}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Sage Transactions */}
          {enrollment.sageTransactions.length > 0 && (
            <>
              <Separator />
              <CollapsibleSection
                defaultOpen={failedSyncCount > 0}
                title={`Sage Transactions (${enrollment.sageTransactions.length})`}
              >
                <div className="space-y-2">
                  {enrollment.sageTransactions.map(
                    (txn: EnrollmentSageTransaction, i: number) => (
                      <SageTransactionCard
                        key={`${txn.transactionType}-${i}`}
                        onRetrySuccess={onRefundSuccess}
                        txn={txn}
                      />
                    ),
                  )}
                </div>
              </CollapsibleSection>
            </>
          )}

          {/* Voucher recognition pending */}
          {enrollment.voucherRecognitionPending && (
            <>
              <Separator />
              <div className="rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 px-3 py-2.5">
                <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                  Voucher Recognition Pending
                </p>
                <p className="text-xs text-amber-600 dark:text-amber-400/80 mt-0.5">
                  The invoice and adjustment for this voucher will be created
                  when the session is finalized.
                </p>
              </div>
            </>
          )}

          {/* Refund Action */}
          {canRefund && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Issue Refund
                </h4>
                {!showRefundForm ? (
                  <Button
                    className="w-full"
                    onClick={() => setShowRefundForm(true)}
                    variant="outline"
                  >
                    Issue Refund
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Textarea
                      className="resize-none text-sm"
                      onChange={(e) => setRefundReason(e.target.value)}
                      placeholder="Reason for refund..."
                      rows={3}
                      value={refundReason}
                    />
                    <div className="flex gap-2">
                      <Button
                        className="flex-1"
                        disabled={isRefunding}
                        onClick={() => {
                          setShowRefundForm(false);
                          setRefundReason("");
                        }}
                        variant="outline"
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1"
                        disabled={!refundReason.trim() || isRefunding}
                        onClick={async () => {
                          setIsRefunding(true);
                          try {
                            await axiosInstance.post(
                              `/api/private/enrollments/${enrollment.enrollmentId}/refund`,
                              {
                                reason: refundReason.trim(),
                                notifyAttendee: true,
                              },
                            );
                            toast({
                              title: "Refund initiated",
                              description: `Refund for ${enrollment.firstName} ${enrollment.lastName} has been initiated.`,
                            });
                            onRefundSuccess();
                            setShowRefundForm(false);
                            setRefundReason("");
                          } catch {
                            // Error toast handled by axios interceptor
                          } finally {
                            setIsRefunding(false);
                          }
                        }}
                        variant="default"
                      >
                        {isRefunding && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Confirm Refund
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Voucher Return Action */}
          {canReturnVoucher && (
            <>
              <Separator />
              <div className="space-y-3">
                <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Return Voucher
                </h4>
                {!showVoucherReturnForm ? (
                  <Button
                    className="w-full"
                    onClick={() => setShowVoucherReturnForm(true)}
                    variant="outline"
                  >
                    Return Voucher
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <Textarea
                      className="resize-none text-sm"
                      onChange={(e) => setVoucherReturnReason(e.target.value)}
                      placeholder="Reason for voucher return..."
                      rows={3}
                      value={voucherReturnReason}
                    />
                    <div className="flex gap-2">
                      <Button
                        className="flex-1"
                        disabled={isReturningVoucher}
                        onClick={() => {
                          setShowVoucherReturnForm(false);
                          setVoucherReturnReason("");
                        }}
                        variant="outline"
                      >
                        Cancel
                      </Button>
                      <Button
                        className="flex-1"
                        disabled={
                          !voucherReturnReason.trim() || isReturningVoucher
                        }
                        onClick={async () => {
                          setIsReturningVoucher(true);
                          try {
                            await axiosInstance.post(
                              `/api/private/enrollments/${enrollment.enrollmentId}/return-voucher`,
                              { reason: voucherReturnReason.trim() },
                            );
                            toast({
                              title: "Voucher returned",
                              description: `Voucher for ${enrollment.firstName} ${enrollment.lastName} has been returned.`,
                            });
                            onRefundSuccess();
                            setShowVoucherReturnForm(false);
                            setVoucherReturnReason("");
                          } catch {
                            // Error toast handled by axios interceptor
                          } finally {
                            setIsReturningVoucher(false);
                          }
                        }}
                        variant="default"
                      >
                        {isReturningVoucher && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Confirm Return
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function SageTransactionCard({
  txn,
  onRetrySuccess,
}: {
  txn: EnrollmentSageTransaction;
  onRetrySuccess?: () => void;
}) {
  const [retrying, setRetrying] = useState(false);
  const [localStatus, setLocalStatus] = useState(txn.syncStatus);
  const [localError, setLocalError] = useState(txn.syncError);
  const { toast } = useToast();

  const handleRetry = async () => {
    setRetrying(true);
    setLocalError(null);
    try {
      const result = await retrySageTransactionSync(txn.id);
      setLocalStatus(result.data.syncStatus);
      if (result.data.syncStatus === "synced") {
        toast({
          title: "Sync successful",
          description: "Transaction synced to Intacct.",
        });
        onRetrySuccess?.();
      } else {
        setLocalError(result.data.syncError || "Sync failed");
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.data?.syncError || err?.message || "Retry failed";
      setLocalError(errorMsg);
      setLocalStatus("failed");
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 text-sm">
      <div className="flex items-start justify-between mb-1">
        <span className="font-medium text-gray-900 dark:text-white">
          {txn.transactionType}
        </span>
        <SyncStatusBadge status={localStatus} />
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
        <p>Amount: {fmt(txn.totalAmount)}</p>
        {txn.referenceId && (
          <p className="text-gray-400 dark:text-gray-500">
            Ref: {txn.referenceId}
          </p>
        )}
      </div>
      {localError && (
        <div className="mt-2 rounded-md bg-red-50 dark:bg-red-900/20 px-2.5 py-2 text-xs text-red-700 dark:text-red-400">
          {localError}
        </div>
      )}
      {(localStatus === "failed" || localStatus === "pending") && (
        <button
          className="mt-2 inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
          disabled={retrying}
          onClick={handleRetry}
          type="button"
        >
          <RotateCcw className={`h-3 w-3 ${retrying ? "animate-spin" : ""}`} />
          {retrying ? "Retrying..." : "Retry sync"}
        </button>
      )}
    </div>
  );
}

function SyncStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    synced: "bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-300",
    pending:
      "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    failed: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  };

  return (
    <span
      className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${styles[status] || styles.pending}`}
    >
      {status}
    </span>
  );
}
